import { useQuery } from "@tanstack/react-query"

import { api } from "@/lib/api"

/**
 * V-Q9: authors suggestion source for CreatorPicker on catalog forms
 * (chapter/album/audioQuote). Distinct from useAllCoaches which lists
 * platform coach records — this hook lists ALL author strings ever
 * used across catalog docs, so admins autocomplete against the names
 * they've historically typed instead of the coach directory.
 *
 * Backend contract (POST /admin/author_list, legacy envelope):
 *   { success, data: [{ name, count }], message }
 *
 * Ranked by usage count desc, tied alpha asc. No pagination — total
 * distinct authors is bounded (~500-2000) so a single fetch is fine
 * for the picker use case. Free-text always allowed on the picker —
 * the response is a suggestion source, not an allowlist.
 */

export type AuthorOption = {
  id: string // stable-per-name key for react lists (name itself)
  name: string
  count: number
}

export function useAllAuthors() {
  return useQuery({
    queryKey: ["admin", "authors", "all"] as const,
    queryFn: async (): Promise<AuthorOption[]> => {
      const res = await api.post<{
        success: boolean
        data: Array<{ name: string; count: number }>
        message: string
      }>("/admin/author_list", {})
      const rows = Array.isArray(res.data?.data) ? res.data.data : []
      return rows
        .filter((r) => typeof r?.name === "string" && r.name.length)
        .map((r) => ({
          id: r.name,
          name: r.name,
          count: typeof r.count === "number" ? r.count : 0,
        }))
    },
    staleTime: 60_000,
  })
}
